/**
 * Solar 2D architecture: layer classification, connection rules, placement hints.
 */

import type { VisualEquipment } from './types';
import { isModuleCleaningSystemAssetType, isModuleLikeAssetType } from './assetTypeConfig';

/** Vertical flow: DC → Inverter → AC → Transmission; other = side box; accessory nests under DC modules */
export type SolarLayer = 'dc' | 'inverter' | 'ac' | 'transmission' | 'other' | 'accessory';

export type SolarConnection = {
  from: SolarLayer;
  to: SolarLayer;
  label: string;
};

/** Ordered main-flow edges (for documentation / future validation) */
export const LAYER_CONNECTIONS: SolarConnection[] = [
  { from: 'dc', to: 'inverter', label: 'DC' },
  { from: 'inverter', to: 'ac', label: 'AC' },
  { from: 'ac', to: 'ac', label: 'LT Panel (logical)' },
  { from: 'ac', to: 'transmission', label: 'LV side' }
];

/** Duplicate naming — canonical display keys */
export const ASSET_TYPE_ALIASES: Record<string, string> = {
  'SPV MODULE': 'PV Array Module',
  'spv module': 'PV Array Module',
  'STRING INVERTER': 'INVERTER',
  'string inverter': 'INVERTER',
  'String Inverter': 'Inverter'
};

export function normalizeAssetTypeName(assetType: string): string {
  const trimmed = assetType.trim();
  const upper = trimmed.toUpperCase();
  if (ASSET_TYPE_ALIASES[trimmed]) return ASSET_TYPE_ALIASES[trimmed];
  if (ASSET_TYPE_ALIASES[upper]) return ASSET_TYPE_ALIASES[upper];
  return trimmed;
}

/**
 * Keyword-based layer classification (plan: NAME-BASED CLASSIFICATION).
 */
export function classifyAssetToLayer(assetType: string): SolarLayer {
  const normalized = normalizeAssetTypeName(assetType);
  const lower = normalized.toLowerCase();

  if (isModuleCleaningSystemAssetType(assetType)) {
    return 'other';
  }
  if (lower.includes('accessory') && (lower.includes('module') || lower.includes('pv'))) {
    return 'accessory';
  }
  if (lower.includes('battery') || lower.includes('ups')) {
    return 'other';
  }
  if (lower.includes('transformer')) {
    return 'transmission';
  }
  if (lower.includes('lt panel') || lower.includes('ltp')) {
    return 'ac';
  }
  if (lower.includes('ac cable')) {
    return 'ac';
  }
  /** String combiner / junction on DC side — part of PV → inverter collection (not "Other Systems") */
  if (lower.includes('combiner') || lower.includes('string combiner')) {
    return 'dc';
  }
  if (lower.includes('junction box')) {
    return 'dc';
  }
  if (lower.includes('inverter')) {
    return 'inverter';
  }
  if (lower.includes('dc cable')) {
    return 'dc';
  }
  if (
    lower.includes('module') ||
    lower.includes('spv') ||
    lower.includes('pv array') ||
    (lower.includes('pv') && !lower.includes('cable'))
  ) {
    return 'dc';
  }
  if (lower.includes('string') && !lower.includes('inverter')) {
    /** Strings in tree map to DC collection / module grouping */
    return 'dc';
  }

  return 'other';
}

export type PlacementCorrection = {
  equipmentId: string;
  assetType: string;
  currentLayer: SolarLayer;
  suggestedLayer: SolarLayer;
  reason: string;
};

/**
 * Returns a suggested layer when the asset is clearly misplaced vs. keyword rules.
 */
export function autoCorrectPlacement(
  equipment: VisualEquipment,
  _allEquipment: VisualEquipment[]
): PlacementCorrection | null {
  const layer = classifyAssetToLayer(equipment.assetType);
  /** Placeholder for future: compare layer to parent chain and return move suggestion */
  void _allEquipment;
  void layer;
  return null;
}

/**
 * Non-blocking warning when hierarchy parent doesn't match solar architecture expectations.
 */
export function getPlacementMismatchMessage(
  equipment: VisualEquipment,
  allEquipment: VisualEquipment[]
): string | null {
  const parent = equipment.parentEquipmentId
    ? allEquipment.find(e => e.id === equipment.parentEquipmentId)
    : null;
  if (!parent) return null;

  const childLayer = classifyAssetToLayer(equipment.assetType);
  const childLower = equipment.assetType.toLowerCase();
  const parentLower = parent.assetType.toLowerCase();

  /** DC Cable: should not hang under transformer / AC-only / batteries */
  if (childLower.includes('dc cable')) {
    if (
      parentLower.includes('transformer') ||
      parentLower.includes('lt panel') ||
      parentLower.includes('ac cable') ||
      parentLower.includes('battery') ||
      parentLower.includes('ups')
    ) {
      return 'DC Cable should sit on the DC path between PV modules and inverter. Consider moving it under modules or inverter.';
    }
  }

  /** AC Cable: should not be under modules-only or transformer-only incorrectly */
  if (childLower.includes('ac cable')) {
    if (
      parentLower.includes('module') ||
      parentLower.includes('spv') ||
      parentLower.includes('string') ||
      parentLower.includes('battery') ||
      parentLower.includes('ups')
    ) {
      return 'AC Cable belongs on the AC path (after inverter, toward LT panel).';
    }
  }

  /** Battery / UPS only — do not treat combiner/junction/other DC gear as battery */
  const childLooksLikeBatteryOrUps =
    childLower.includes('battery') ||
    childLower.includes('ups') ||
    /\bbess\b/.test(childLower);
  if (childLayer === 'other' && childLooksLikeBatteryOrUps) {
    if (
      parentLower.includes('inverter') ||
      isModuleLikeAssetType(parent.assetType) ||
      parentLower.includes('module') ||
      parentLower.includes('string') ||
      parentLower.includes('combiner')
    ) {
      return 'Battery / UPS equipment is shown in Other Systems — keep it separate from the main PV–inverter flow unless explicitly required.';
    }
  }

  /** PV Module Accessories: should stay with module family */
  if (childLayer === 'accessory') {
    const parentIsModuleLike =
      isModuleLikeAssetType(parent.assetType) ||
      parentLower.includes('string') ||
      parentLower.includes('module');
    if (!parentIsModuleLike) {
      return 'PV Module Accessories should be grouped with PV modules (under String or Module).';
    }
  }

  return null;
}
